/**
 * OperatedFocusContext — Sprint 4.5 paymaster theme shift.
 *
 * Lets a child component declare "I'm currently focused on operated
 * company X". The shell reads the focused id + colour and uses it to
 * override the navbar accent + sidebar bar so the visual context
 * matches the data being viewed.
 *
 * This is opt-in: components that don't set focus get the default
 * (paymaster's own colour). When a focused component unmounts or
 * blurs, the focus clears and the shell reverts.
 *
 * The council's UX recommendation was: "make cross-tenant context
 * unmistakable in the UI so paymaster admins never forget whose data
 * they're touching." The banner conveys this verbally; the colour
 * shift conveys it pre-attentively.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface OperatedFocus {
  companyId: string;
  companyName: string;
  companyColour: string | null;
}

interface OperatedFocusContextValue {
  focus: OperatedFocus | null;
  setFocus: (focus: OperatedFocus | null) => void;
}

const OperatedFocusContext = createContext<OperatedFocusContextValue>({
  focus: null,
  setFocus: () => {},
});

export function OperatedFocusProvider({ children }: { children: ReactNode }) {
  const [focus, setFocusState] = useState<OperatedFocus | null>(null);
  const setFocus = useCallback((next: OperatedFocus | null) => {
    setFocusState(next);
  }, []);
  const value = useMemo(() => ({ focus, setFocus }), [focus, setFocus]);
  return (
    <OperatedFocusContext.Provider value={value}>{children}</OperatedFocusContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOperatedFocus() {
  return useContext(OperatedFocusContext);
}
